import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { API_URLS } from '../../config/api';
import { 
  TrendingUp, 
  Users, 
  Briefcase, 
  Receipt, 
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Calendar,
  Clock,
  Star,
  Monitor,
  Smartphone,
  Globe
} from 'lucide-react';

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { adminApiFetch } = await import('../../utils/adminApi');
      const response = await adminApiFetch(`${API_URLS.ADMIN.ADMIN_API}?action=getAnalytics`);
      const result = await response.json();
      
      if (result.status === 'success') {
        setAnalytics(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Analytics verilerinden istatistikler hesapla
  const totalVisits = analytics.length;
  const uniqueIPs = new Set(analytics.map(visit => visit.ip_address)).size;
  const todayVisits = analytics.filter(visit => {
    const visitDate = new Date(visit.visit_date);
    const today = new Date();
    return visitDate.toDateString() === today.toDateString();
  }).length;

  // En çok ziyaret edilen sayfalar
  const pageVisits = analytics.reduce((acc, visit) => {
    const page = visit.page_url || '/';
    acc[page] = (acc[page] || 0) + 1;
    return acc;
  }, {});

  const topPages = Object.entries(pageVisits)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5);

  // Cihaz türleri
  const deviceTypes = analytics.reduce((acc, visit) => {
    const userAgent = visit.user_agent || '';
    let deviceType = 'Desktop';
    if (userAgent.includes('Mobile') || userAgent.includes('Android') || userAgent.includes('iPhone')) {
      deviceType = 'Mobile';
    } else if (userAgent.includes('Tablet') || userAgent.includes('iPad')) {
      deviceType = 'Tablet';
    }
    acc[deviceType] = (acc[deviceType] || 0) + 1;
    return acc;
  }, {});

  const stats = [
    {
      title: 'Toplam Ziyaret',
      value: totalVisits.toString(),
      change: '+12%',
      changeType: 'positive',
      icon: Globe,
      color: 'bg-blue-500',
      description: 'Tüm zamanlar'
    },
    {
      title: 'Benzersiz Ziyaretçi',
      value: uniqueIPs.toString(),
      change: '+8%',
      changeType: 'positive',
      icon: Users,
      color: 'bg-green-500',
      description: 'Farklı IP adresi'
    },
    {
      title: 'Bugünkü Ziyaret',
      value: todayVisits.toString(),
      change: '+23%',
      changeType: 'positive',
      icon: Calendar,
      color: 'bg-orange-500',
      description: 'Bugün'
    },
    {
      title: 'Mobil Ziyaret',
      value: deviceTypes.Mobile || '0',
      change: '-5%',
      changeType: 'negative',
      icon: Smartphone,
      color: 'bg-purple-500',
      description: 'Mobil cihazlar'
    }
  ];

  const recentUsers = [
    { 
      id: 1, 
      name: 'Ahmet Yılmaz', 
      email: 'ahmet@example.com', 
      status: 'active', 
      avatar: 'AY',
      joinDate: '2 saat önce',
      level: 'Premium'
    },
    { 
      id: 2, 
      name: 'Fatma Demir', 
      email: 'fatma@example.com', 
      status: 'pending', 
      avatar: 'FD',
      joinDate: '1 gün önce',
      level: 'Basic'
    },
    { 
      id: 3, 
      name: 'Mehmet Kaya', 
      email: 'mehmet@example.com', 
      status: 'active', 
      avatar: 'MK',
      joinDate: '3 gün önce',
      level: 'Pro'
    },
    { 
      id: 4, 
      name: 'Ayşe Özkan', 
      email: 'ayse@example.com', 
      status: 'inactive', 
      avatar: 'AÖ',
      joinDate: '1 hafta önce',
      level: 'Basic'
    },
  ];

  // Son ziyaretler
  const recentVisits = analytics
    .sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date))
    .slice(0, 10)
    .map(visit => ({
      id: visit.id,
      ip: visit.ip_address,
      page: visit.page_url,
      browser: visit.user_agent?.includes('Chrome') ? 'Chrome' :
               visit.user_agent?.includes('Firefox') ? 'Firefox' :
               visit.user_agent?.includes('Safari') ? 'Safari' : 'Diğer',
      device: visit.user_agent?.includes('Mobile') ? 'Mobile' : 'Desktop',
      visitDate: new Date(visit.visit_date).toLocaleString('tr-TR'),
      referrer: visit.referrer || 'Direkt'
    }));

  const recentJobs = [
    { 
      id: 1, 
      title: 'Web Geliştirme Projesi', 
      client: 'Tech Corp', 
      budget: '₺5,000', 
      status: 'active',
      postedDate: '2 saat önce',
      category: 'Web Geliştirme'
    },
    { 
      id: 2, 
      title: 'Mobil Uygulama Tasarımı', 
      client: 'Startup Inc', 
      budget: '₺3,500', 
      status: 'completed',
      postedDate: '1 gün önce',
      category: 'Mobil'
    },
    { 
      id: 3, 
      title: 'SEO Optimizasyonu', 
      client: 'E-ticaret Ltd', 
      budget: '₺2,000', 
      status: 'pending',
      postedDate: '3 gün önce',
      category: 'SEO'
    },
    { 
      id: 4, 
      title: 'Logo Tasarımı', 
      client: 'Creative Agency', 
      budget: '₺800', 
      status: 'active',
      postedDate: '1 hafta önce',
      category: 'Tasarım'
    },
  ];

  const chartData = [
    { name: 'Oca', users: 400, revenue: 2400 },
    { name: 'Şub', users: 300, revenue: 1398 },
    { name: 'Mar', users: 200, revenue: 9800 },
    { name: 'Nis', users: 278, revenue: 3908 },
    { name: 'May', users: 189, revenue: 4800 },
    { name: 'Haz', users: 239, revenue: 3800 },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Premium': return 'bg-purple-100 text-purple-800';
      case 'Pro': return 'bg-blue-100 text-blue-800';
      case 'Basic': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Website Al platform genel durumu</p>
        </div>
        <div className="flex items-center gap-2 sm:space-x-3">
          <Button variant="outline" size="sm" className="text-xs sm:text-sm">
            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
            <span className="hidden sm:inline">Son 30 Gün</span>
            <span className="sm:hidden">30 Gün</span>
          </Button>
          <Button size="sm" className="text-xs sm:text-sm">
            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
            <span className="hidden sm:inline">Rapor İndir</span>
            <span className="sm:hidden">Rapor</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {stats.map((stat, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 truncate">
                    {stat.title}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                    {stat.value}
                  </p>
                  <div className="flex items-center space-x-1 sm:space-x-2 flex-wrap">
                    {stat.changeType === 'positive' ? (
                      <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                    ) : (
                      <ArrowDownRight className="w-3 h-3 sm:w-4 sm:h-4 text-red-600 flex-shrink-0" />
                    )}
                    <span className={`text-xs sm:text-sm font-medium ${
                      stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.change}
                    </span>
                    <span className="text-xs sm:text-sm text-gray-500 hidden sm:inline">
                      {stat.description}
                    </span>
                  </div>
                </div>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg ${stat.color} flex items-center justify-center flex-shrink-0 ml-2`}>
                  <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Revenue Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center justify-between text-base sm:text-lg">
              <span>Gelir ve Kullanıcı Analizi</span>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <div className="h-64 sm:h-80 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center px-4">
                <TrendingUp className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-2 sm:mb-4" />
                <p className="text-sm sm:text-base text-gray-600">Grafik bileşeni burada gösterilecek</p>
                <p className="text-xs sm:text-sm text-gray-500 mt-2">Recharts entegrasyonu için hazır</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Site Ziyaretçileri */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg">Site Ziyaretçileri</CardTitle>
          </CardHeader>
          <CardContent className="p-0 sm:p-6">
            {/* Mobile View - Card Layout */}
            <div className="block sm:hidden">
              <div className="space-y-3 p-4">
                {analytics.slice(0, 5).map((visit) => (
                  <div key={visit.id} className="border-b border-gray-200 pb-3 last:border-0 last:pb-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs text-gray-900 truncate">{visit.ip_address}</div>
                        <div className="text-xs text-gray-600 truncate mt-1">{visit.page_url}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                      <span>
                        {visit.user_agent ? (
                          visit.user_agent.includes('Chrome') ? 'Chrome' :
                          visit.user_agent.includes('Firefox') ? 'Firefox' :
                          visit.user_agent.includes('Safari') ? 'Safari' :
                          visit.user_agent.includes('Edge') ? 'Edge' : 'Diğer'
                        ) : '-'}
                      </span>
                      <span>•</span>
                      <span className="truncate">
                        {new Date(visit.visit_date).toLocaleDateString('tr-TR', { 
                          day: '2-digit', 
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {analytics.length > 5 && (
                <div className="px-4 pb-4 text-center">
                  <Button variant="outline" size="sm" className="text-xs">
                    Tümünü Gör ({analytics.length} ziyaret)
                  </Button>
                </div>
              )}
            </div>

            {/* Desktop View - Table Layout */}
            <div className="hidden sm:block">
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="w-full min-w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-xs sm:text-sm">IP Adresi</th>
                      <th className="text-left py-3 px-4 text-xs sm:text-sm">Sayfa</th>
                      <th className="text-left py-3 px-4 text-xs sm:text-sm hidden lg:table-cell">Referrer</th>
                      <th className="text-left py-3 px-4 text-xs sm:text-sm">Tarayıcı</th>
                      <th className="text-left py-3 px-4 text-xs sm:text-sm hidden md:table-cell">Ziyaret Tarihi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.slice(0, 10).map((visit) => (
                      <tr key={visit.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="font-mono text-xs sm:text-sm">{visit.ip_address}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-xs sm:text-sm text-gray-900 truncate max-w-xs">{visit.page_url}</div>
                        </td>
                        <td className="py-3 px-4 hidden lg:table-cell">
                          <div className="text-xs sm:text-sm text-gray-600">
                            {visit.referrer ? (
                              <a href={visit.referrer} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block max-w-xs">
                                {visit.referrer.length > 30 ? visit.referrer.substring(0, 30) + '...' : visit.referrer}
                              </a>
                            ) : (
                              'Direkt'
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-xs sm:text-sm text-gray-600">
                            {visit.user_agent ? (
                              <span title={visit.user_agent}>
                                {visit.user_agent.includes('Chrome') ? 'Chrome' :
                                 visit.user_agent.includes('Firefox') ? 'Firefox' :
                                 visit.user_agent.includes('Safari') ? 'Safari' :
                                 visit.user_agent.includes('Edge') ? 'Edge' : 'Diğer'}
                              </span>
                            ) : '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-xs sm:text-sm text-gray-600 hidden md:table-cell">
                          {new Date(visit.visit_date).toLocaleString('tr-TR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {analytics.length > 10 && (
                <div className="mt-4 text-center">
                  <Button variant="outline" size="sm">
                    Tümünü Gör ({analytics.length} ziyaret)
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

export default AdminDashboard; 